import PropTypes from 'prop-types';
import { Formik, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Row, Col, Form, Button } from 'react-bootstrap';
import Tippy from '@tippyjs/react';
import { ToggleSwitch } from '../common';

const SendRawDataSettings = ({ sendRawData, handleUpdate }) => (
  <Formik
    initialValues={{
      isSendRawDataEnabled: sendRawData?.enabled ?? false,
      fileFormat: sendRawData?.fileFormat ?? 'json',
      frequency: sendRawData?.frequency ?? 'twice-a-day',
      recipientEmail: sendRawData?.email ?? '',
    }}
    enableReinitialize
    validationSchema={Yup.object({
      fileFormat: Yup.string().when('isSendRawDataEnabled', {
        is: true,
        then: Yup.string().required('File format is required'),
      }),
      frequency: Yup.string().when('isSendRawDataEnabled', {
        is: true,
        then: Yup.string().required('Frequency is required'),
      }),
      recipientEmail: Yup.string().when('isSendRawDataEnabled', {
        is: true,
        then: Yup.string()
          .email('Invalid email format')
          .required('Recipient email is required'),
      }),
    })}
    onSubmit={(values) => {
      handleUpdate({
        sendRawData: values.isSendRawDataEnabled,
        fileFormat: values.fileFormat,
        frequency: values.frequency,
        recipientEmail: values.recipientEmail,
      });
    }}
  >
    {(formikprops) => (
      <div className="col-12 col-md-12">
        <Form onSubmit={formikprops.handleSubmit} className="needs-validation">
          {/* Header */}
          <Row>
            <Col>
              <h4 className="font-weight-base mb-1">Send Raw Data</h4>
              <small className="text-muted">
                Automatically send raw data reports to a specified email address
                at a defined frequency. These reports include only{' '}
                <strong>schedule</strong> and <strong>work-session</strong>{' '}
                related data from the last two days. All timestamps in the
                exported document are provided in UTC.
              </small>
            </Col>
            <Col className="col-auto">
              <ToggleSwitch
                id="isSendRawDataEnabled"
                name="isSendRawDataEnabled"
                handleChange={() => {
                  formikprops.setFieldValue(
                    'isSendRawDataEnabled',
                    !formikprops.values.isSendRawDataEnabled
                  );

                  // Update on disabling the toggle
                  if (!formikprops.values.isSendRawDataEnabled === false) {
                    handleUpdate({
                      sendRawData: !formikprops.values.isSendRawDataEnabled,
                      fileFormat: formikprops.values.fileFormat,
                      frequency: formikprops.values.frequency,
                      recipientEmail:
                        formikprops.values.recipientEmail &&
                        formikprops.values.recipientEmail !== ''
                          ? formikprops.values.recipientEmail
                          : sendRawData.email,
                    });
                  }
                }}
                checked={formikprops.values.isSendRawDataEnabled}
              />
            </Col>
          </Row>

          <br />

          {formikprops.values.isSendRawDataEnabled && (
            <div>
              {/* File Format */}
              <Row>
                <Col className="col pt-2" style={{ maxWidth: '160px' }}>
                  <small>File Format</small>
                </Col>

                <Col className="px-2 col-auto">
                  <Field
                    as="select"
                    className="form-select form-select-sm"
                    name="fileFormat"
                    style={{
                      width: '250px',
                      marginTop: '6px',
                    }}
                  >
                    <option value="json">JSON</option>
                    <option value="csv">CSV</option>
                  </Field>
                </Col>

                <ErrorMessage
                  name="fileFormat"
                  render={(msg) => <small className="text-danger">{msg}</small>}
                />
              </Row>

              {/* Frequency */}
              <Row>
                <Col className="col pt-2" style={{ maxWidth: '160px' }}>
                  <small>Frequency</small>
                </Col>

                <Col className="px-2 col-auto">
                  <Field
                    as="select"
                    className="form-select form-select-sm"
                    name="frequency"
                    style={{
                      width: '250px',
                      marginTop: '6px',
                    }}
                  >
                    <option value="twice-a-day">Twice a day</option>
                    <option value="once-a-day">Once a day</option>
                  </Field>
                </Col>
                <Col className="p-0">
                  <Tippy
                    content={
                      <small
                        className="text-muted"
                        style={{ whiteSpace: 'pre-line' }}
                      >
                        Once a day = midnight.
                        {'\n'}
                        Twice a day = midnight + afternoon.
                        {'\n'}
                        (All timestamps are in UTC)
                      </small>
                    }
                    theme="light"
                  >
                    <span className="pt-2 d-inline-block">
                      <i className="mdi mdi-information-outline" />
                    </span>
                  </Tippy>
                </Col>

                <ErrorMessage
                  name="frequency"
                  render={(msg) => <small className="text-danger">{msg}</small>}
                />
              </Row>

              {/* Recipient Email */}
              <Row>
                <Col className="col pt-2" style={{ maxWidth: '160px' }}>
                  <small>Recipient Email</small>
                </Col>

                <Col className="px-2 col-auto">
                  <Field
                    type="email"
                    name="recipientEmail"
                    placeholder="example@domain.com"
                    className="form-control form-control-sm"
                    style={{
                      width: '250px',
                      marginTop: '6px',
                    }}
                  />
                </Col>
                <ErrorMessage
                  name="recipientEmail"
                  render={(msg) => <small className="text-danger">{msg}</small>}
                />
              </Row>

              <br />

              {/* Save Button */}
              <Row>
                <Col md="auto">
                  <Button
                    size="sm"
                    type="submit"
                    style={{ height: '26px' }}
                    disabled={!formikprops.isValid}
                  >
                    Save
                  </Button>
                </Col>
              </Row>
            </div>
          )}
        </Form>
      </div>
    )}
  </Formik>
);

SendRawDataSettings.propTypes = {
  sendRawData: PropTypes.shape({
    enabled: PropTypes.bool,
    frequency: PropTypes.string,
    fileFormat: PropTypes.string,
    email: PropTypes.string,
  }),
  handleUpdate: PropTypes.func,
};

export default SendRawDataSettings;
